import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Cookie, Settings2, ShieldCheck, X } from "lucide-react";

const STORAGE_KEY = "baugrundcheck_cookie_consent";
const CONSENT_VERSION = 1;

/** Read current consent (or null if none yet). */
function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(values) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    ...values,
  }));
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    // Defer slightly so it doesn't interfere with initial paint
    const timer = setTimeout(() => {
      if (!readConsent()) setVisible(true);
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  const acceptAll = () => {
    writeConsent({ essential: true, decision: "all" });
    setVisible(false);
    setShowSettings(false);
  };

  const acceptEssentialOnly = () => {
    writeConsent({ essential: true, decision: "essential_only" });
    setVisible(false);
    setShowSettings(false);
  };

  if (!visible) return null;

  return (
    <>
      <div
        data-testid="cookie-banner"
        className="fixed inset-x-0 bottom-0 z-50 p-4 md:p-6 pointer-events-none"
      >
        <div className="container mx-auto pointer-events-auto">
          <div className="bg-[#1A2F24] text-white rounded-xl shadow-2xl border border-[#2E3B32] overflow-hidden">
            <div className="relative grid grid-cols-1 md:grid-cols-[1fr,auto] gap-5 md:gap-8 p-5 md:p-6 items-start md:items-center">
              <div className="flex gap-4">
                <div className="hidden sm:flex shrink-0 w-10 h-10 rounded-md bg-white/10 border border-white/15 items-center justify-center">
                  <Cookie size={20} />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-semibold text-base mb-1.5">
                    Cookies &amp; Datenschutz
                  </div>
                  <p className="text-sm text-white/75 leading-relaxed">
                    BaugrundCheck.de verwendet ausschließlich technisch notwendige Cookies für Login &amp; Sitzung — kein Tracking, keine Marketing-Cookies.
                    Mehr in unserer{" "}
                    <Link to="/datenschutz" className="text-[#E89F8C] hover:text-white underline underline-offset-2" data-testid="cookie-banner-privacy-link">
                      Datenschutzerklärung
                    </Link>.
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <Button
                  data-testid="cookie-banner-settings"
                  onClick={() => setShowSettings(true)}
                  variant="outline"
                  className="bg-transparent border-white/25 text-white hover:bg-white/10 hover:text-white"
                >
                  <Settings2 size={14} className="mr-1.5" /> Einstellungen
                </Button>
                <Button
                  data-testid="cookie-banner-accept"
                  onClick={acceptAll}
                  className="bg-[#C05A44] hover:bg-[#A64B38] text-white"
                >
                  Verstanden
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-md" data-testid="cookie-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ShieldCheck size={20} className="text-[#1A2F24]" /> Cookie-Einstellungen
            </DialogTitle>
            <DialogDescription>
              Sie entscheiden, welche Cookies wir setzen dürfen. Wir nutzen aktuell nur technisch notwendige Cookies.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="bg-[#F1EFE7] border border-[#E5E2D9] rounded-lg p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-medium text-[#1A2F24]">
                    Technisch notwendig
                    <span className="text-[10px] uppercase tracking-wider bg-[#1A2F24] text-white px-1.5 py-0.5 rounded">
                      Pflicht
                    </span>
                  </div>
                  <p className="text-xs text-[#5C6B61] mt-1.5 leading-relaxed">
                    JWT-Session-Cookies (httpOnly) für Ihre Anmeldung. Ohne diese Cookies können Sie die Anwendung nicht nutzen.
                  </p>
                </div>
                <Switch
                  data-testid="cookie-essential-switch"
                  checked
                  disabled
                  className="shrink-0"
                />
              </div>
            </div>

            <div className="bg-white border border-[#E5E2D9] rounded-lg p-4 opacity-70">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#1A2F24]">Analyse &amp; Marketing</div>
                  <p className="text-xs text-[#5C6B61] mt-1.5 leading-relaxed">
                    Aktuell nicht im Einsatz. BaugrundCheck.de setzt kein Tracking, kein Pixel und keine Drittanbieter-Cookies für Werbung.
                  </p>
                </div>
                <Switch
                  data-testid="cookie-marketing-switch"
                  checked={false}
                  disabled
                  className="shrink-0"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              data-testid="cookie-settings-essential-only"
              onClick={acceptEssentialOnly}
              variant="outline"
              className="border-[#1A2F24] text-[#1A2F24]"
            >
              Nur Notwendige
            </Button>
            <Button
              data-testid="cookie-settings-accept-all"
              onClick={acceptAll}
              className="bg-[#1A2F24] hover:bg-[#2E3B32] text-white"
            >
              Alle akzeptieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Public helper: re-open settings later (e.g. footer link). */
export function clearCookieConsent() {
  localStorage.removeItem(STORAGE_KEY);
}
